/**
 * Décision de contrôle d'un titre — cœur de la PWA Verify.
 *
 * Fonction **pure** : elle reçoit le contenu scanné, la clé publique, l'heure et
 * l'éventuel avis du serveur, et rend un verdict. Aucun accès réseau, aucun
 * état. C'est ce qui la rend testable exhaustivement, et c'est elle qui décide
 * ce qu'un agent lit à bout de bras sur un pont.
 *
 * ## Deux questions distinctes, et il faut les tenir séparées
 *
 * 1. **L'authenticité** — ce document a-t-il été émis par l'État ? Elle se
 *    tranche **hors ligne**, par la signature Ed25519, avec la seule clé
 *    publique embarquée.
 * 2. **La validité** — ce titre est-il encore en vigueur ? L'expiration se lit
 *    hors ligne, sur les dates scellées. La **révocation**, non : elle survient
 *    après l'émission, donc rien dans le document ne peut la porter. Elle exige
 *    le réseau.
 *
 * Un titre authentique mais suspendu resterait déclaré valide par le seul
 * contrôle hors ligne. Le verdict le **dit** plutôt que de le taire : c'est la
 * différence entre un outil honnête et un outil qui rassure à tort.
 */

import { analyserContenuQr, verifierNumeroOfficiel } from '@sygap/core/navigateur';

/** Les cinq états, et rien d'autre. Un agent décide en deux secondes. */
export const VERDICT = {
  /** Authentique, en cours de validité, non révoqué. */
  VALIDE: 'valide',
  /** Authentique, mais la date d'expiration est passée. */
  EXPIRE: 'expire',
  /** Authentique, mais suspendu ou annulé par l'administration. */
  RETIRE: 'retire',
  /** La signature ne correspond pas : document fabriqué ou altéré. */
  FAUX: 'faux',
  /** Illisible, ou inconnu du registre. */
  INCONNU: 'inconnu',
} as const;
export type Verdict = (typeof VERDICT)[keyof typeof VERDICT];

/** Ce que le serveur a répondu, quand le réseau était là. */
export interface AvisServeur {
  /** `valide`, `expire`, `revoque` ou `inconnu`, tel que rendu par `/verify/:uuid`. */
  readonly resultat: string;
}

/** Données lisibles du titre, extraites du QR. */
export interface TitreScanne {
  readonly type: string;
  readonly numeroOfficiel: string;
  readonly immatriculation: string;
  readonly dateEmission: string;
  readonly dateExpiration: string;
}

export interface Decision {
  readonly verdict: Verdict;
  /** Titre lu ; `null` si le contenu était illisible. */
  readonly titre: TitreScanne | null;
  /**
   * `true` quand la révocation n'a **pas** pu être contrôlée faute de réseau.
   * L'interface doit le dire : un titre suspendu ce matin paraîtrait valide.
   */
  readonly revocationNonControlee: boolean;
  /** Explication en une phrase, destinée à l'agent. */
  readonly explication: string;
}

/** Forme attendue du payload signé, version 1. */
interface PayloadTitreV1 {
  readonly v: number;
  readonly type: string;
  readonly uuid: string;
  readonly numero: string;
  readonly immatriculation: string;
  readonly emission: string;
  readonly expiration: string;
}

function estPayloadV1(valeur: unknown): valeur is PayloadTitreV1 {
  if (typeof valeur !== 'object' || valeur === null) return false;
  const o = valeur as Record<string, unknown>;
  return (
    o.v === 1 &&
    typeof o.type === 'string' &&
    typeof o.uuid === 'string' &&
    typeof o.numero === 'string' &&
    typeof o.immatriculation === 'string' &&
    typeof o.emission === 'string' &&
    typeof o.expiration === 'string'
  );
}

/** Extrait l'UUID d'un contenu scanné, pour interroger le serveur si possible. */
export function uuidDuContenu(contenu: string): string | null {
  const analyse = analyserContenuQr(contenu);
  if (analyse === null) return null;
  try {
    const payload: unknown = JSON.parse(analyse.canonique);
    return estPayloadV1(payload) ? payload.uuid : null;
  } catch {
    return null;
  }
}

export interface ContexteDecision {
  /** Contenu brut du QR Code. */
  readonly contenu: string;
  /** Verdict de la signature, déjà calculé — la vérification est asynchrone. */
  readonly signatureValide: boolean;
  readonly maintenant: Date;
  /** Réponse du serveur, `null` si le contrôle s'est fait hors ligne. */
  readonly avisServeur: AvisServeur | null;
}

/**
 * Rend le verdict.
 *
 * L'ordre des contrôles est celui de la gravité décroissante : un document faux
 * ne devient pas « expiré » parce que sa date est passée — il reste faux, et
 * c'est cela qu'il faut montrer à l'agent.
 */
export function decider(contexte: ContexteDecision): Decision {
  const analyse = analyserContenuQr(contexte.contenu);
  if (analyse === null) {
    return {
      verdict: VERDICT.INCONNU,
      titre: null,
      revocationNonControlee: false,
      explication: "Ce code n'est pas un titre SYGAP.",
    };
  }

  let payload: unknown;
  try {
    payload = JSON.parse(analyse.canonique);
  } catch {
    return {
      verdict: VERDICT.INCONNU,
      titre: null,
      revocationNonControlee: false,
      explication: 'Contenu illisible. Réessayez le scan.',
    };
  }

  if (!estPayloadV1(payload)) {
    return {
      verdict: VERDICT.INCONNU,
      titre: null,
      revocationNonControlee: false,
      // Un QR d'une version future n'est pas un faux : c'est un vérificateur trop ancien.
      explication: 'Version de titre non reconnue. Mettez l’application à jour.',
    };
  }

  const titre: TitreScanne = {
    type: payload.type,
    numeroOfficiel: payload.numero,
    immatriculation: payload.immatriculation,
    dateEmission: payload.emission,
    dateExpiration: payload.expiration,
  };

  // 1. Authenticité. Le contrôle le plus grave passe en premier.
  if (!contexte.signatureValide) {
    return {
      verdict: VERDICT.FAUX,
      titre,
      revocationNonControlee: false,
      explication: 'La signature ne correspond pas. Ce document n’a pas été émis par l’État.',
    };
  }

  // Le caractère de contrôle du numéro est redondant avec la signature, mais il
  // écarte une incohérence interne : un numéro mal formé dans un payload
  // pourtant signé signale une anomalie d'émission, pas une falsification.
  if (!verifierNumeroOfficiel(titre.numeroOfficiel).valide) {
    return {
      verdict: VERDICT.INCONNU,
      titre,
      revocationNonControlee: false,
      explication: 'Numéro officiel incohérent. Signalez ce titre à votre administration.',
    };
  }

  // 2. Expiration — lisible hors ligne, sur les dates scellées.
  if (estEchu(titre.dateExpiration, contexte.maintenant)) {
    return {
      verdict: VERDICT.EXPIRE,
      titre,
      revocationNonControlee: false,
      explication: `Titre authentique, mais expiré depuis le ${enJourLisible(titre.dateExpiration)}.`,
    };
  }

  // 3. Révocation — impossible à lire hors ligne : elle survient après l'émission.
  if (contexte.avisServeur === null) {
    return {
      verdict: VERDICT.VALIDE,
      titre,
      revocationNonControlee: true,
      explication: `Titre authentique, valable jusqu’au ${enJourLisible(titre.dateExpiration)}. Suspension éventuelle non vérifiée : pas de réseau.`,
    };
  }

  if (contexte.avisServeur.resultat === 'revoque') {
    return {
      verdict: VERDICT.RETIRE,
      titre,
      revocationNonControlee: false,
      explication: 'Titre suspendu ou annulé par l’administration.',
    };
  }

  if (contexte.avisServeur.resultat === 'inconnu') {
    // Signature valide mais UUID absent du registre : incohérence grave, à
    // traiter comme un faux plutôt que comme une simple absence.
    return {
      verdict: VERDICT.FAUX,
      titre,
      revocationNonControlee: false,
      explication: 'Ce titre est introuvable au registre national.',
    };
  }

  if (contexte.avisServeur.resultat === 'expire') {
    return {
      verdict: VERDICT.EXPIRE,
      titre,
      revocationNonControlee: false,
      explication: 'Titre expiré selon le registre national.',
    };
  }

  return {
    verdict: VERDICT.VALIDE,
    titre,
    revocationNonControlee: false,
    explication: `Titre valide jusqu’au ${enJourLisible(titre.dateExpiration)}.`,
  };
}

/** Échu si la date d'expiration est strictement antérieure au jour courant (UTC). */
function estEchu(dateExpiration: string, maintenant: Date): boolean {
  return dateExpiration < maintenant.toISOString().slice(0, 10);
}

/** `2027-07-27` → `27/07/2027`. */
export function enJourLisible(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  if (annee === undefined || mois === undefined || jour === undefined) return iso;
  return `${jour}/${mois}/${annee}`;
}
